import { useContext, useMemo } from 'react';
import WLContext from 'labels/context';
import { useSelector } from 'react-redux';
import { rtlEnable } from 'domain/env';

const useDefaultThemeConfig = () => {
  const {
    labelOptions: { styles: wlThemeStyles, ...wlThemeConfig },
  } = useContext(WLContext);
  const isRtl = useSelector(rtlEnable);

  return useMemo(
    () => ({
      ...wlThemeStyles,
      palette: {
        ...wlThemeStyles.palette,
        // Chat-only off-white (#FBFDFF) surface for the conversation right side
        // (message list + input + empty state), excluding the white header.
        // Fixed value, not white-label-brandable. Consumed by EmptyPanelContent
        // (via theme) and mapped to the `--dokka-chat-surface` CSS var in
        // ChatProvider's colorSet for the Sendbird `.sendbird-conversation` element.
        chat: {
          ...wlThemeStyles.palette?.chat,
          surface: 'rgba(251, 253, 255, 1)',
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*': {
              // [MUI] - needs for JE after migrate to mui
              margin: 0,
              padding: 0,
              fontFamily: ['Rubik', 'Helvetica', 'Arial', 'sans-serif'].join(','),
            },
            body: {
              direction: 'ltr',
            },
            '#root': {
              minHeight: '100vh',
              display: 'flex',
            },
            '.UserSelect-none': {
              userSelect: 'none',
            },
            // hide widget launcher iframe via css, because sometimes freshdesk widget dont want hide through
            // window.FreshworksWidget('hide', 'launcher');
            // I guess Freshdesk script is not yet initialized when hide API is invoked
            // 1 - always hide using styles/css
            // 2- try hide use widget API after 1second timeout - see HelpDropdown component
            '#launcher-frame': {
              display: 'none',
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: ({ theme }) => ({
              fontSize: theme.typography.h6,
            }),
          },
        },
        MuiButtonGroup: {
          styleOverrides: {
            root: {
              borderRadius: 100,
            },
            contained: {
              boxShadow: 'none', // only affects contained buttons
            },
          },
        },
        MuiButton: {
          defaultProps: {
            variant: 'contained',
          },
          styleOverrides: {
            root: {
              borderRadius: 100,
            },
            contained: {
              boxShadow: 'none', // only affects contained buttons
            },
          },
        },
        // TODO: check behaviour - having stuck tooltips for disabled IconButtons
        // cant use code below
        // MuiIconButton: {
        //   styleOverrides: {
        //     root: {
        //       '&[disabled]': {
        //         cursor: 'not-allowed',
        //         pointerEvents: 'auto',
        //       },
        //     },
        //   },
        // },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme }) => ({
              backgroundColor: theme.palette.grey[800],
              padding: theme.spacing(0.25, 1),
            }),
            arrow: ({ theme }) => ({
              color: theme.palette.grey[800],
            }),
          },
        },
      },
      direction: isRtl ? 'rtl' : 'ltr',
      typography: {
        fontFamily: ['Rubik', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        fontWeightSemiBold: 600,
        hint: {
          fontSize: '0.75rem',
        },
      },
      mixins: {
        toolbar: {
          minHeight: 64,
        },
      },
      labelOptions: wlThemeConfig,
    }),
    [isRtl, wlThemeConfig, wlThemeStyles],
  );
};

export default useDefaultThemeConfig;
